import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AuthBrandPanel, BrandMark } from './AuthBrandPanel';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const betaHighlights = [
  "See what's happening. Act before it escalates.",
  'Guidance grounded in FOS precedent.',
  'Built for outcomes.',
];

const betaModules = [
  {
    title: 'Case Guidance',
    description: 'Relevant FOS precedent at the point of decision.',
  },
  {
    title: 'Juno',
    description: 'Conduct intelligence across guidance, classification, and reporting.',
  },
  {
    title: 'Conduct Records',
    description: 'Structured complaints and feedback in one shared record.',
  },
];

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white lg:flex">
      <AuthBrandPanel highlights={betaHighlights} />

      <div className="flex-1 bg-figma-bg-main">
        <div className="mx-auto flex min-h-screen w-full max-w-3xl flex-col px-4 py-6 sm:px-6 lg:px-10 lg:py-8">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 lg:hidden">
              <BrandMark />
              <div>
                <p className="text-lg font-semibold text-figma-text-primary">Good Outcomes</p>
                <p className="text-sm text-figma-text-secondary">
                  Conduct intelligence for Consumer Duty
                </p>
              </div>
            </div>

            <Button
              asChild
              variant="ghost"
              className="ml-auto text-primary hover:bg-primary/5 hover:text-primary"
            >
              <Link to="/manifesto">Manifesto</Link>
            </Button>
          </div>

          <div className="my-auto space-y-6 py-8">
            <Card className="border-border bg-card shadow-sm">
              <CardHeader className="space-y-3">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-primary">
                  Private beta
                </p>
                <CardTitle className="text-3xl font-semibold text-foreground sm:text-4xl">
                  Built for outcomes.
                </CardTitle>
                <p className="text-base leading-7 text-muted-foreground">
                  Already have access? Sign in. New here? Read the manifesto to see what
                  Good Outcomes is building.
                </p>
              </CardHeader>

              <CardContent className="space-y-5">
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button asChild size="lg" className="w-full sm:w-auto">
                    <Link to="/auth" className="inline-flex items-center gap-2">
                      Sign in to the beta
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
                    <Link to="/manifesto">Read the manifesto</Link>
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground">
                  Built by the team behind DataEQ.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-sm">
              <CardHeader className="space-y-3">
                <CardTitle className="text-2xl font-semibold text-foreground">
                  In the beta today.
                </CardTitle>
                <p className="text-base leading-7 text-muted-foreground">
                  Relevant precedent, clearer records, and help at the point of decision.
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-hidden rounded-lg border border-border bg-background p-3">
                  <img
                    src="/clusters-dashboard.png"
                    alt="Good Outcomes beta dashboard showing conduct intelligence patterns and signal groupings."
                    className="w-full rounded-md object-cover"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border bg-card shadow-sm">
              <CardContent className="p-0">
                <div className="divide-y divide-border">
                  {betaModules.map((module) => (
                    <div
                      key={module.title}
                      className="grid gap-2 px-6 py-5 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-6"
                    >
                      <div className="text-lg font-semibold text-foreground">{module.title}</div>
                      <p className="text-sm leading-6 text-muted-foreground sm:text-base">
                        {module.description}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
